import React, { ComponentType } from "react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "../../yoello-lib/modules/auth/Auth0";
import { useSnackbarMessages } from "../../yoello-lib/modules/components/snackbar/SnackbarContext";
import Login from "./LoginComponent";
import SmsConfirmation from "./SmsConfirmation";
import "./Login.css";
import T from "i18n-react";
import { ConsumerCreate, getConsumer } from "../../Api.js";
import { useSelector } from "react-redux";
import Signup from "./Signup";
import PageLoading from "../Page/PageLoading";
import Styles from "./styles.module.scss";
import NavigationHeader from "../NavigationHeader/NavigationHeader";
import PageComponent from "../Page/PageComponent";
import { IPageComponentProps } from "../Page/types.d";

export enum LoginStages {
  LOGIN = "login",
  SIGNUP = "signup",
  ONETIMECODE = "onetimecode",
  LOADING = "loading",
}

function LoginContainer({ openSideDrawer }: IPageComponentProps) {
  const {
    retrieveUser,
    passwordlessLogin,
    passwordlessVerifiySMS,
    isAuthenticated,
    token,
    setUpAuthedUser,
    redirectURL,
  } = useAuth0()!;
  const { sendSnackbarMessage } = useSnackbarMessages()!;
  const [loginStage, setLoginStage] = useState<LoginStages>(LoginStages.LOGIN);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [oneTimeCode, setOneTimeCode] = useState("");
  const [smsLoading, setSmsLoading] = useState(false);
  const RedirectToPage = useSelector(
    (state: any) => state.LoginRedirectToMainPage
  );
  const Venue = useSelector((state: any) => state.Venue);
  const GroupName = useSelector((state: any) => state.GroupName);
  const history = useHistory();
  useEffect(() => {
    // if a calling code is stored in local storage then we use that
    if (window.localStorage.getItem("USER_country_calling_code")) {
      setPhoneNumber(window.localStorage.getItem("USER_country_calling_code"));
    } else {
      setPhoneNumber("+44 ");
    }
    //eslint-disable-next-line
  }, []);
  useEffect(() => {
    /**Verifies the sms code automatically */
    if (oneTimeCode.length === 6 && !isLoading) {
      verifiySMSCode();
    }
    //eslint-disable-next-line
  }, [oneTimeCode]);
  useEffect(() => {
    /**if the user has become authenticated try to use the onAuthenticated flow */
    if (isAuthenticated) {
      setLoginStage(LoginStages.LOADING);
      onAuthenticatedUser().catch(onNoConsumerDetails);
    }
    //eslint-disable-next-line
  }, [isAuthenticated, token]);
  useEffect(() => {
    /**Helps default back to loading if there is an access token */
    if (window.location.hash.includes("access")) {
      setLoginStage(LoginStages.LOADING);
    }
    /**onload tries the retrieve user flow */
    retrieveUser()
      .then(() => {
        /**We rely on the events firing off once the auth context is done */
      })
      .catch((e) => {
        if (localStorage.getItem("user_token") !== null) {
          localStorage.removeItem("user_token");
        }
        setLoginStage(LoginStages.LOGIN);
      });
    //eslint-disable-next-line
  }, []);
  /**Tries and gets the consumer account and navigates the user */
  async function onAuthenticatedUser() {
    if (token !== undefined) {
      const consumer = await getConsumer(token.token);
      if (consumer.data.data.user_details_set) {
        setUpAuthedUser(consumer.data.data, token);

        if (RedirectToPage === "PaymentA" || RedirectToPage === "PaymentV") {
          history.push("/payment", {
            loggedIn: true,
            area: RedirectToPage === "PaymentA",
          });
        } else {
          if (redirectURL) {
            history.push(redirectURL);
          } else {
            history.push(getBackUrl());
          }
        }
      } else {
        throw new Error(T.translate("Login.Errors.NoUserDetails") as string);
      }
    }
  }
  /**When the user doesn't have login details, we attempt to create them a consumer account
   * and redirects them to the signup page
   */
  async function onNoConsumerDetails() {
    if (token !== undefined) {
      try {
        await ConsumerCreate({}, token.token);
        setLoginStage(LoginStages.SIGNUP);
      } catch (error) {
        if (error.response) {
          if (error.response.status === 412) {
            setLoginStage(LoginStages.SIGNUP);
            return;
          }
        }
        sendSnackbarMessage(
          T.translate("Login.Errors.LoginWithNewAccountError") as string,
          "error"
        );
      }
    } else {
      setLoginStage(LoginStages.LOGIN);
    }
  }
  function getBackUrl() {
    if (Venue === undefined) {
      return "/";
    }
    switch (RedirectToPage) {
      case "Home":
        return "/";
      case "VenueV":
        return "/v/" + Venue.venue_nickname;
      case "VenueA":
        return `/g/${GroupName}/${Venue.venue_nickname}`;
      case "MenuA":
        return `/g/${GroupName}/${Venue.venue_nickname}`;
      case "MenuV":
        return "/v/" + Venue.venue_nickname + "/menu";
      case "PaymentV":
        return "/v/" + Venue.venue_nickname + "/menu";
      case "PaymentA":
        return `/g/${GroupName}/${Venue.venue_nickname}`;
      case "VenuesA":
        return `/g/${GroupName}`;
      default:
        return "/";
    }
  }
  async function verifiySMSCode() {
    setIsLoading(true);
    try {
      await passwordlessVerifiySMS(
        phoneNumber.replace(/[\s()-]/g, "").trim(),
        oneTimeCode
      );
      setLoginStage(LoginStages.LOADING);
    } catch (error) {
      sendSnackbarMessage(
        T.translate("Login.Errors.InvalidCode") as string,
        "error"
      );
    } finally {
      setIsLoading(false);
      setOneTimeCode("");
    }
  }

  function resendSMS() {
    if (smsLoading) {
      sendSnackbarMessage(
        T.translate("Login.Errors.AlreadySentSms") as string,
        "error"
      );
    } else {
      setSmsLoading(true);
      sendSnackbarMessage(
        T.translate("Login.Errors.SmsSent") as string,
        "success"
      );
      login();
      setTimeout(() => {
        setSmsLoading(false);
      }, 30000);
    }
  }
  async function login() {
    setIsLoading(true);
    try {
      await passwordlessLogin(phoneNumber.replace(/[\s()-]/g, "").trim());
      setLoginStage(LoginStages.ONETIMECODE);
      setCountryCode();
    } catch (error) {
      if (error.statusCode === 400) {
        sendSnackbarMessage(
          T.translate("Login.Errors.ValidPhone") as string,
          "error"
        );
      }
    } finally {
      setIsLoading(false);
    }
  }

  function setCountryCode() {
    const dialingCode = phoneNumber.split(" ")[0];
    if (
      window.localStorage.getItem("USER_country_calling_code") !== dialingCode
    ) {
      window.localStorage.setItem("USER_country_calling_code", dialingCode);
    }
  }

  function renderStage() {
    switch (loginStage) {
      case LoginStages.LOADING:
        return <PageLoading />;
      case LoginStages.LOGIN:
        return (
          <Login
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            loginLoading={isLoading}
            loginFunction={login}
          />
        );
      case LoginStages.ONETIMECODE:
        return (
          <SmsConfirmation
            phoneNumber={phoneNumber}
            oneTimeCode={oneTimeCode}
            setOneTimeCode={setOneTimeCode}
            resendSMS={resendSMS}
          />
        );
      case LoginStages.SIGNUP:
        return (
          <Signup
            token={token!.token}
            onAuthenticatedUser={onAuthenticatedUser}
          />
        );
      default:
        return <p>{T.translate("Login.Texts.LoginIssue")}</p>;
    }
  }

  function getBackButton() {
    switch (loginStage) {
      case LoginStages.LOGIN:
        return () => history.push(getBackUrl());
      default:
        return () => setLoginStage(LoginStages.LOGIN);
    }
  }
  return (
    <div data-component-name="LoginContainer">
      <NavigationHeader
        backURL={getBackButton()}
        openSideDrawer={openSideDrawer}
      />
      {/* <div className={Styles.BackButtonDiv}>{getBackButton()}</div> */}
      <div className={Styles.Login}>
        <div
          style={{
            marginTop: "5rem",
            marginBottom: "4rem",
          }}
        ></div>
        <div
          style={{
            padding: "0.5rem",
          }}
        >
          {renderStage()}
        </div>
      </div>
    </div>
  );
}

export default PageComponent(LoginContainer as ComponentType);
